// @flow

import React, { type Element, useState } from 'react'
import { Container, Grid, Typography, Link, Button } from '@material-ui/core'
import { MuiThemeProvider } from '@material-ui/core/styles'
import PasswordField from '../CreateLoginForm/PasswordField'
import AlertError from '../../Common/AlertError'
import PasswordConfirmationField from '../CreateLoginForm/PasswordConfirmationField'
import FormData from './FormData'
import HiddenForm from './HiddenForm'
import ValidationResult from './ValidationResult'
import theme from '../../../theme'

type Props = {|
  resetPasswordToken: string,
  serverErrors: { [string]: Array<string> },
|}

function PasswordReset({ resetPasswordToken, serverErrors }: Props): Element<'div'> {
  const formId: string = 'user_password'
  const [validationResult, setValidationResult] = useState(new ValidationResult())
  const [formData, setFormData] = useState(new FormData())

  const handleValidationResultChange = (fieldName: $Keys<ValidationResult>, isValid: boolean) => {
    setValidationResult((prevValue) => prevValue.update(fieldName, isValid))
  }
  const handleFormDataChange = (fieldName: $Keys<FormData>, value: string) => {
    setFormData((prevValue) => prevValue.update(fieldName, value))
  }

  const handleGoToDashboard = (event: SyntheticInputEvent<HTMLElement>): void => {
    const form: ?HTMLElement = document.getElementById(formId)
    if (form instanceof HTMLFormElement) {
      form.submit()
    } else {
      const error = new Error('Unable to find formId user_password in the password reset page')
      window.newrelic.noticeError(error)
    }
  }

  return (
    <MuiThemeProvider theme={theme}>
      <Container maxWidth="sm">
        <Grid item xs={12}>
          {Object.keys(serverErrors).length > 0 && <AlertError errors={serverErrors} />}
        </Grid>
      </Container>
      <Container maxWidth="sm">
        <HiddenForm
          formData={{
            formId,
            resetPasswordToken,
            password: formData.password,
          }}
        />
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h3">Change your password</Typography>
          </Grid>
          <Grid item xs={12}>
            <PasswordField
              value={formData.password}
              setIsValid={(isValid) => handleValidationResultChange('password', isValid)}
              setValue={(value) => handleFormDataChange('password', value)}
            />
          </Grid>
          <Grid item xs={12}>
            <PasswordConfirmationField
              password={formData.password}
              isPasswordValid={validationResult.password}
              setIsValid={(isValid) =>
                handleValidationResultChange('passwordConfirmation', isValid)
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!validationResult.isValid()}
              onClick={handleGoToDashboard}
              form={formId}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
        <Grid container spacing={1} style={{ paddingTop: 12 }}>
          <Grid item xs={12}>
            <Link href="/users/sign_in">Sign in</Link>
          </Grid>
          <Grid item xs={12}>
            <Link href="/users/sign_up">Sign up</Link>
          </Grid>
          <Grid item xs={12}>
            <Link href="/users/unlock/new">Didn't receive unlock instructions?</Link>
          </Grid>
        </Grid>
      </Container>
    </MuiThemeProvider>
  )
}

export default PasswordReset
